import { faChevronDown, faChevronUp, faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, DatePicker, Drawer, Form, Input, InputNumber, Radio, Select } from "antd";
import { useState } from "react";
import Spinner from "@src/components/spinner";
import { useMutation } from "@apollo/client";
import { upsertKeyholdingSiteBillingMutation } from "@src/util/mutation/keyholdingSiteBilling.mutation";
import { useDispatch } from "react-redux";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";


const { TextArea } = Input;

export const SetupBillingDrawer = ({ editData, visible, close, refetch, siteId=null }) => {


    const param = useParams();

    const [upsertKeyholdingSiteBilling, {loading:upsertBillingLoading}] = useMutation(upsertKeyholdingSiteBillingMutation);


    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState({
        name: editData?.name ?? '',
        rate: editData?.rate ?? 0,
        currency: editData?.currency ?? 'GBP',
        per: editData?.per ?? 'year',
        startDate: editData?.startDate ?? null,
        paymentTiming: editData?.paymentTiming ?? 'upfront',
        description: editData?.description ?? '',
        autoRenew: editData?.autoRenew ?? true,
        terminationGraceDays: editData?.terminationGraceDays ?? 7,
    });

    const handleValuesChange = (changedValues, allValues) => {
        setFormValues(allValues);
    };

    const dispatch = useDispatch();

    const handleSubmit = async() => {
        await form.validateFields().then(async (values) => {
           
            await upsertKeyholdingSiteBilling({
                variables:{
                    input: {...values,  siteId: (param?.id || siteId), _id: editData?._id}
                }
            });
            await refetch();

            close();

        });

        dispatch(
            setNotification({
                error: false,
                notificationState: true,
                message: "Action was successful"
            })
        );
    };

    const calculateNextBillingDate = () => {
        if (!formValues.startDate) return 'Not set';
        const start = new Date(formValues.startDate);
        const next = new Date(start);
        
        if (formValues.per === 'month') {
            next.setMonth(next.getMonth() + 1);
        } else {
            next.setFullYear(next.getFullYear() + 1);
        }
        
        return next.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getCurrencySymbol = () => {
        switch(formValues.currency) {
            case 'USD': return '$';
            case 'EUR': return '€';
            case 'GBP':
            default: return '£';
        }
    };

    return (
        <Drawer
            title="Setup Site Recurring Billing"
            open={visible}
            placement="right"
            closable={true}
            onClose={close}
            closeIcon={<FontAwesomeIcon icon={faClose} onClick={close} className='close-icon'/>}
            width={600}
            maskClosable={false}
            mask={true}
            footer={
                <div className='drawer-footer'>
                    <button 
                        disabled={loading || upsertBillingLoading} 
                        className={(loading || upsertBillingLoading )? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'} 
                        onClick={handleSubmit}
                    >
                        {(loading || upsertBillingLoading) ? <Spinner color={"#ff7a53"}/>  : editData?.name? "Update" : 'Create'} 
                    </button>
                    <button disabled={loading || upsertBillingLoading} className='drawer-outlined-btn' onClick={close}>Cancel</button>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                initialValues={{
                    name: editData?.name ?? '',
                    rate: editData?.rate ?? 0,
                    currency: editData?.currency ?? 'GBP',
                    per: editData?.per ?? 'year',
                    startDate: dayjs(editData?.startDate) ?? null,
                    paymentTiming: editData?.paymentTiming ?? 'upfront',
                    description: editData?.description ?? '',
                    autoRenew: editData?.autoRenew ?? true,
                    terminationGraceDays: editData?.terminationGraceDays ?? 7,
                }}
            >
                <Form.Item
                    label="Plan Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter a plan name' }]}
                >
                    <Input 
                        placeholder="e.g., Premium Keyholding Service" 
                        className="generic-input-control"
                        value={formValues.name}
                    />
                </Form.Item>


                <Form.Item
                    label="Rate"
                    name="rate"
                    rules={[{ required: true, message: 'Please enter a rate' }]}
                >
                    <InputNumber 
                        min={0}
                        step={0.01}
                        placeholder="0.00"
                        style={{ margin: 0, padding: '3px 0', width: '100%' }}
                        upHandler={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faChevronUp} />}
                        downHandler={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faChevronDown} />}
                        className="generic-input-control"
                        value={formValues?.rate}
                    />
                </Form.Item>

                {/* <Form.Item
                    label="Billing Period"
                    name="per"
                    rules={[{ required: true }]}
                >
                    <Radio.Group style={{marginTop:'0'}}>
                        <Radio name="per" value="month"> Monthly </Radio>
                        <Radio name="per" value="year"> Yearly </Radio>
                    </Radio.Group>
                </Form.Item> */}

                <Form.Item
                    label="Billing Period"
                    name="per"
                    rules={[{ required: true }]}
                    style={{ marginBottom: '24px' }}
                >
                    <Radio.Group style={{marginTop:'8px'}} value={formValues?.per}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div style={{ 
                                border: formValues.per === 'month' ? '2px solid #0091ae' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '16px',
                                cursor: 'pointer',
                                backgroundColor: formValues.per === 'month' ? '#f0f9ff' : 'transparent',
                                transition: 'all 0.3s ease',
                                textAlign: 'center'
                            }}>
                                <Radio value="month" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ 
                                        fontSize: '20px', 
                                        fontWeight: 700, 
                                        color: formValues.per === 'month' ? '#0091ae' : '#666',
                                        marginBottom: '4px' 
                                    }}>
                                        Monthly
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#999' }}>
                                        Billed every month
                                    </div>
                                </Radio>
                            </div>
                            
                            <div style={{ 
                                border: formValues.per === 'year' ? '2px solid #0091ae' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '16px',
                                cursor: 'pointer',
                                backgroundColor: formValues.per === 'year' ? '#f0f9ff' : 'transparent',
                                transition: 'all 0.3s ease',
                                textAlign: 'center',
                                position: 'relative'
                            }}>
                                <Radio value="year" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ 
                                        fontSize: '20px', 
                                        fontWeight: 700, 
                                        color: formValues.per === 'year' ? '#0091ae' : '#666',
                                        marginBottom: '4px' 
                                    }}>
                                        Yearly
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#999' }}>
                                        Billed annually
                                    </div>
                                </Radio>
                            </div>
                        </div>
                    </Radio.Group>
                </Form.Item>



                <Form.Item
                    label="Billing Start Date"
                    name="startDate"
                    rules={[{ required: true, message: 'Please select a start date' }]}
                >
                    <DatePicker 
                        placeholder="Select billing start date"
                        className="generic-input-control"
                        style={{ width: '100%' }}
                        format={"DD/MM/YYYY"}
                    />
                </Form.Item>
                <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginBottom: '24px' 
                }}>
                    {formValues.paymentTiming === 'upfront' 
                        ? 'First payment will be charged on this date'
                        : 'Service begins on this date, payment due after first period'}
                </div>


                <Form.Item
                    label="Payment Due"
                    name="paymentTiming"
                    rules={[{ required: true }]}
                    style={{ marginBottom: '24px' }}
                >
                    <Radio.Group style={{marginTop:'8px'}}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ 
                                border: formValues.paymentTiming === 'upfront' ? '2px solid #0091ae' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                cursor: 'pointer',
                                backgroundColor: formValues.paymentTiming === 'upfront' ? '#f0f9ff' : 'transparent'
                            }}>
                                <Radio value="upfront" style={{ alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                            Upfront 
                                            {/* <span style={{ fontSize: '12px', color: '#52c41a' }}>(Recommended)</span> */}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            Payment collected immediately upon signup.
                                        </div>
                                    </div>
                                </Radio>
                            </div>
                            
                            <div style={{ 
                                border: formValues.paymentTiming === 'arrears' ? '2px solid #0091ae' : '1px solid #d9d9d9',
                                borderRadius: '6px',
                                padding: '12px',
                                cursor: 'pointer',
                                backgroundColor: formValues.paymentTiming === 'arrears' ? '#f0f9ff' : 'transparent'
                            }}>
                                <Radio value="arrears" style={{ alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>In Arrears</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            Payment collected after the first billing period ends.
                                        </div>
                                    </div>
                                </Radio>
                            </div>
                        </div>
                    </Radio.Group>
                </Form.Item>
                

                <Form.Item
                    label="Description (Optional)"
                    name="description"
                >
                    <TextArea 
                        placeholder="Additional details about this billing plan..."
                        rows={3}
                        className="generic-input-control"
                    />
                </Form.Item>

                <Form.Item
                    name="autoRenew"
                    valuePropName="checked"
                    style={{ marginBottom: '24px' }}
                >
                    <Checkbox>
                        Enable automatic renewal 
                        {/* <span style={{ fontSize: '12px', color: '#52c41a' }}>(recommended)</span> */}
                    </Checkbox>
                </Form.Item>

                {/* Termination Grace Period */}
                <div style={{
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '24px'
                }}>
                    <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                        Termination Policy
                    </div>
                    
                    <Form.Item
                        label="Grace Period (Days before billing date)"
                        name="terminationGraceDays"
                        rules={[{ required: true, message: 'Please enter grace period days' }]}
                        style={{ marginBottom: '8px' }}
                    >
                        <InputNumber 
                            min={1}
                            max={28}
                            placeholder="7"
                            style={{ width: '100%' }}
                            upHandler={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faChevronUp} />}
                            downHandler={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faChevronDown} />}
                            className="generic-input-control"
                        />
                    </Form.Item>
                    
                    <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                        If termination occurs within <strong>{formValues.terminationGraceDays || 7} days</strong> before the billing date, 
                        no charge will be applied for that period.
                        <div style={{ marginTop: '8px', color: '#fa8c16' }}>
                            <strong>Example:</strong> With 7 days grace period, if billing date is on the 15th and termination 
                            is before the 8th, no charge. If terminated on or after the 8th, full period charge applies.
                        </div>
                    </div>
                </div>

                {/* Billing Summary */}
                <div style={{
                    background: 'linear-gradient(135deg, #e6f7ff 0%, #e6fffb 100%)',
                    border: '2px solid #0091ae',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '24px'
                }}>
                    <div style={{ 
                        fontWeight: 600, 
                        fontSize: '16px', 
                        marginBottom: '16px',
                        color: '#000'
                    }}>
                        Billing Summary
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Recurring charge:</span>
                            <span style={{ fontWeight: 600 }}>
                                {getCurrencySymbol()}{formValues.rate || '0.00'} / {formValues.per === 'month' ? 'month' : 'year'}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Payment timing:</span>
                            <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {formValues.paymentTiming}
                            </span>
                        </div>
                        
                        <div style={{ 
                            borderTop: '1px solid #91d5ff', 
                            paddingTop: '10px', 
                            marginTop: '6px',
                            display: 'flex', 
                            justifyContent: 'space-between' 
                        }}>
                            <span style={{ fontWeight: 600 }}>First charge:</span>
                            <span style={{ fontWeight: 700, fontSize: '16px' }}>
                                {getCurrencySymbol()}{formValues.rate || '0.00'}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Next billing:</span>
                            <span style={{ fontWeight: 600 }}>
                                {calculateNextBillingDate()}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Auto-renewal:</span>
                            <span style={{ fontWeight: 600 }}>
                                {formValues.autoRenew ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666' }}>Termination grace:</span>
                            <span style={{ fontWeight: 600 }}>
                                {formValues.terminationGraceDays || 7} days before billing
                            </span>
                        </div>
                    </div>
                </div>
            </Form>
        </Drawer>
    );
};